import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostListener,
    Input,
    OnInit,
    QueryList,
    ViewChild,
    ViewChildren,
    Output,
    EventEmitter,
    ViewRef
} from '@angular/core';
import { BehaviorSubject, Observable, of, timer } from 'rxjs';
import { distinctUntilChanged, debounce } from 'rxjs/operators';
import { AutoCompleteV3Service } from './auto-complete-v3.service';
import { AcsListItemV3Component } from './components/acs-list-item-v3/acs-list-item-v3.component';
import { Base64 } from 'js-base64';
import { AutoCompleteConfigInterface, AutoCompleteListItemInterface } from './interfaces/auto-complete.interfaces';
import {
    AutoCompleteRequestSourcesEnum,
    AutoCompleteThemeEnum,
    AutoCompleteTypeEnum,
    AutoCompleteKeyboardEnum
} from './enums/auto-complete.enums';
import { EnvironmentService, SiteSettingsService, SubscriptionCollector, TranslationService } from '@draeger-academy/common';
import { ThemeType } from '../da-button/shared/enums/theme-type.enum';
import { AppInfoService } from '@draeger-academy/common/lib/services/app-info/app-info.service';
import { ListKeyManager } from '@angular/cdk/a11y';

const DEFAULT_CONFIG: AutoCompleteConfigInterface = {
    context: AutoCompleteRequestSourcesEnum.SP,
    theme: AutoCompleteThemeEnum.PAGE,
    minCharsTypeAhead: 3,
    debounceTime: 200,
    displayMaxResults: 5,
    maxRequestResults: 200,
    goToTarget: true,
    showAllResultsLink: true
};

@Component({
    selector: 'da-auto-complete-search-v3',
    templateUrl: './auto-complete-search-v3.component.html',
    styleUrls: ['./auto-complete-search-v3.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AutoCompleteSearchV3Component implements OnInit {
    @ViewChild('searchForm', { static: true }) searchForm: ElementRef;
    @ViewChild('searchInput', { static: true }) searchInput: ElementRef;

    @Input() type: AutoCompleteTypeEnum = AutoCompleteTypeEnum.REQUEST;

    @Input() set config(value: AutoCompleteConfigInterface) {
        this._config = {
            ...DEFAULT_CONFIG,
            ...{
                label: this._translationService.getTranslation('SearchWidget-Placeholder'.toLowerCase())
            },
            ...this._config,
            ...value
        };

        this.componentId = `autoCompleteSearch${this.config.theme.charAt(0).toUpperCase() + this.config.theme.slice(1)}`;

        this._initConfigResultUrl();
        this._initConfigGoToPage();
        this._initConfigUrlParameters();

        if (this.config.listItems && this.config.listItems.length > 0) {
            this._$inputWidth.next(this.searchInput.nativeElement.getBoundingClientRect().width);
            this._setNewList(this.config.listItems);
        }

        if (this.config.theme === AutoCompleteThemeEnum.FILTER && this.config.listItems && this.config.listItems.length === 0) {
            this._setNewList([]);
        }
    }
    get config(): AutoCompleteConfigInterface {
        return this._config;
    }

    @Input() set searchTerm(value: string) {
        this._searchTerm = value;
        this.searchInput.nativeElement.value = this._searchTerm;
    }
    get searchTerm(): string {
        return this._searchTerm;
    }

    // TODO: Check is this still needed
    @Input() isMobileMode: boolean;
    @Input() isMobileOpen: boolean;

    @Output() selected: EventEmitter<AutoCompleteListItemInterface | string> = new EventEmitter();
    @Output() onFocus: EventEmitter<boolean> = new EventEmitter(false);
    @Output() onBlur: EventEmitter<boolean> = new EventEmitter(false);

    private _keyManager: ListKeyManager<AcsListItemV3Component>;

    private _$results: BehaviorSubject<Array<AutoCompleteListItemInterface>> = new BehaviorSubject(null);
    private _$lastResultSet: BehaviorSubject<Array<AutoCompleteListItemInterface>> = new BehaviorSubject([]);
    private _$searchString: BehaviorSubject<string> = new BehaviorSubject('');
    private _$searchResultPageUrl: BehaviorSubject<string> = new BehaviorSubject('');
    private _$inputWidth: BehaviorSubject<string> = new BehaviorSubject('');
    private _$hiddenFields: BehaviorSubject<Array<{ key: string; value: string }>> = new BehaviorSubject(null);

    @SubscriptionCollector()
    private _subscriptions;
    private _isFirstRequest = true;

    private _config: AutoCompleteConfigInterface;
    private _searchTerm = '';

    public resultsHidden = true;
    public componentId: string;

    public searchResultPageUrl$: Observable<string> = this._$searchResultPageUrl.asObservable();
    public inputWidth$: Observable<string> = this._$inputWidth.asObservable();
    public results$: Observable<Array<AutoCompleteListItemInterface>> = this._$results.asObservable();
    public hiddenFields$: Observable<Array<{ key: string; value: string }>> = this._$hiddenFields.asObservable();

    public noTheme: ThemeType = ThemeType.None;
    private _showAllResults = 'showAllResults';
    public showAllResultsLinkData = { label: this._showAllResults };
    private _items: QueryList<AcsListItemV3Component>;

    currentIndex;

    @ViewChildren(AcsListItemV3Component) set items(items: QueryList<AcsListItemV3Component>) {
        this._items = items;
        this._keyManager = new ListKeyManager(items).withWrap();
    }
    get items(): QueryList<AcsListItemV3Component> {
        return this._items;
    }

    get showClearButton$(): Observable<boolean> {
        return of(
            ((this.searchTerm && this.searchTerm !== '') || (this._$searchString.getValue() && this._$searchString.getValue() !== '')) &&
                (this.config.theme === 'NOBUTTON' || this.config.theme === AutoCompleteThemeEnum.FILTER)
        );
    }

    get showDropDownButton$(): Observable<boolean> {
        return of(
            !this.searchTerm &&
                !this._$searchString.getValue() &&
                this.config.theme === AutoCompleteThemeEnum.FILTER &&
                this.config.listItems &&
                this.config.listItems.length > 0
        );
    }

    @HostListener('window:resize')
    onResize() {
        this._$inputWidth.next(this.searchInput.nativeElement.getBoundingClientRect().width);
    }

    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _translationService: TranslationService,
        private _autoCompleteService: AutoCompleteV3Service,
        private _environmentService: EnvironmentService,
        private _appInfoService: AppInfoService,
        protected _siteSettingsService: SiteSettingsService //public _logger: DraegerLogger
    ) {}

    public ngOnInit(): void {
        if (this.config == null) {
            this.config = {};
        }
        let _searchTermOld = '';
        this._subscriptions.push(
            this._$searchString
                .pipe(
                    debounce(() => {
                        return this._isFirstRequest ? timer(0) : timer(this.config.debounceTime);
                    }),
                    distinctUntilChanged()
                )
                .subscribe((searchTerm) => {
                    if (searchTerm.length >= this.config.minCharsTypeAhead) {
                        const _lastResultSet = this._$lastResultSet.getValue();
                        const inRangeForLocalResultList =
                            _lastResultSet && _lastResultSet.length > 0 && _lastResultSet.length < this.config.maxRequestResults;

                        if (
                            (this.config.listItems && this.config.listItems.length > 0) ||
                            (inRangeForLocalResultList && searchTerm.startsWith(_searchTermOld))
                        ) {
                            this._useLocalList(searchTerm);
                        } else if (
                            this.config.listItems &&
                            this.config.listItems.length === 0 &&
                            this.config.theme === AutoCompleteThemeEnum.FILTER
                        ) {
                            this._setNewList([]);
                        } else if (
                            this._isFirstRequest ||
                            !searchTerm.startsWith(_searchTermOld) ||
                            (searchTerm.startsWith(_searchTermOld) &&
                                searchTerm !== _searchTermOld &&
                                _lastResultSet &&
                                _lastResultSet.length > 0)
                        ) {
                            this._isFirstRequest = false;
                            this._requestResultList(searchTerm).then((result) => {
                                this._setNewList(result);
                            });
                        }
                    } else if (searchTerm.length < this.config.minCharsTypeAhead) {
                        this._isFirstRequest = true;
                    }
                    _searchTermOld = searchTerm;
                })
        );
    }

    private _initConfigResultUrl() {
        if (this.config.requestUrl == null) {
            let _url = '';
            if (this._environmentService['apiEndpoints'].proxy && this._environmentService['apiEndpoints'].proxy.v1.autocomplete)
                _url = this._environmentService['apiEndpoints'].proxy.v1.autocomplete.v1.suggestion;
            const _site = this._appInfoService.AppInfo.site;
            const _context = this.config.context;
            this.config.requestUrl = `${_url}/${_site}/${_context}`;
        }
    }

    private _initConfigGoToPage() {
        try {
            if (this.config.goToTarget) {
                this._$searchResultPageUrl.next(this.config.targetUrl || this._siteSettingsService.getValue('SearchWidget').ResultPageUrl);
            } else {
                this._$searchResultPageUrl.next('');
            }
        } catch (e) {
            //this._logger.warn('"SearchWidget" not found in configuration');
        }
    }

    private _initConfigUrlParameters() {
        if (this.config.urlParameters) {
            const _urlParameters = [];
            Object.keys(this.config.urlParameters).forEach((key) => {
                _urlParameters.push({
                    key,
                    value: this.config.urlParameters[key]
                });
            });
            this._$hiddenFields.next(_urlParameters);
        }
    }

    public canSubmit(event: Event): void {
        if (this.config.goToTarget) {
            event.preventDefault();
        }
    }

    public highlightSearchTerm(result: AutoCompleteListItemInterface): string {
        return this._autoCompleteService.getHighlightedString(result, this._$searchString.getValue());
    }

    public onKeyDownEvent(event: KeyboardEvent): void {
        if (this._$results.getValue() === null || (this._$results.getValue() && this._$results.getValue().length === 0)) {
            return;
        }
        this._keyManager.onKeydown(event);
        if (this._keyManager.activeItem != null && (event.target as HTMLInputElement).value !== '') {
            this._keyManager.activeItem.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }

    public onKeyUpEvent(event: KeyboardEvent): void {
        // tslint:disable-next-line: deprecation
        const _keyCode = event.keyCode || event.key.charCodeAt(0);
        if (_keyCode === AutoCompleteKeyboardEnum.ENTER) {
            if (this._keyManager.activeItem === undefined) {
                const _checkValueExist =
                    this.config.listItems &&
                    this.config.listItems.find((item) => item.label.toLowerCase() === this.searchTerm.toLowerCase());
                if (!!_checkValueExist) {
                    this.submitForm(_checkValueExist);
                    this.resultsHidden = true;
                } else {
                    this.submitForm();
                }
            } else if (this._keyManager && this._keyManager.activeItem && this._keyManager.activeItem.getLabel() === this._showAllResults) {
                this.submitForm();
            } else if (this._keyManager.activeItem != null) {
                this.searchInput.nativeElement.value = this._keyManager.activeItem.getLabel();
                this.submitForm();
            }
        } else if (_keyCode === AutoCompleteKeyboardEnum.ESCAPE) {
            this.resultsHidden = true;
        } else {
            this.resultsHidden = false;
            const _searchString = (event.target as HTMLInputElement).value;
            const _searchCache = this._$searchString.getValue();
            if (_searchString === _searchCache) {
                return;
            }
            if (_searchString.length < this.config.minCharsTypeAhead) {
                this._setNewList([]);
                this._isFirstRequest = true;
            }
            this._searchTerm = _searchString;
            this._$searchString.next(_searchString);
        }
    }

    public clickedOutside(): void {
        this.resultsHidden = true;
    }

    public getSearchAllHref(): string {
        const _url = this._$searchResultPageUrl.getValue();
        const _searchString = this._$searchString.getValue();
        // TODO: search string as base64?
        // console.log(btoa(_searchString));
        const _urlParameters = [`s=${encodeURIComponent(_searchString)}`];
        if (this.config.urlParameters) {
            Object.keys(this.config.urlParameters).forEach((key) => {
                if (key !== 's') {
                    _urlParameters.push(`${key}=${encodeURIComponent(this.config.urlParameters[key])}`);
                }
            });
        }

        return this.config.goToTarget ? `${_url}?${_urlParameters.join('&')}` : '';
    }

    public submitForm(searchValue?: AutoCompleteListItemInterface): void {
        if (searchValue) {
            this.searchInput.nativeElement.value = searchValue.label ? searchValue.label : searchValue;
        }

        this.searchInput.nativeElement.value = this.searchInput.nativeElement.value.trim().replace(/ +/g, ' ');
        if (this.searchInput.nativeElement.value) {
            const searchString = this.searchInput.nativeElement.value;
            this._setNewList([]);
            this._$searchString.next(searchString);
            if (this.config.goToTarget) {
                this.searchForm.nativeElement.submit();
            } else {
                let _emitValue = searchValue || {
                    label: searchString,
                    value: searchString
                };
                if (searchValue || this._keyManager.activeItem != null) {
                    _emitValue = searchValue || {
                        label: this._keyManager.activeItem.getLabel(),
                        value: this._keyManager.activeItem.getValue()
                    };
                }
                this.selected.emit(_emitValue);
            }
        }
        if (
            this.searchInput.nativeElement.value === '' &&
            (this.config.requestUrl === 'document-explorer' || this.config.theme === AutoCompleteThemeEnum.FILTER) &&
            this.type === AutoCompleteTypeEnum.STATICLIST
        ) {
            this.selected.emit({
                label: '',
                value: ''
            });
        }
    }

    private _requestResultList(searchString: string): Promise<AutoCompleteListItemInterface[]> {
        const _searchString = searchString.trim().replace(/ +/g, ' ');
        this._$inputWidth.next(this.searchInput.nativeElement.getBoundingClientRect().width);

        const _autoCompleteCall = this._autoCompleteService.getSuggestions(
            this.config.requestUrl,
            _searchString,
            this.config.theme === AutoCompleteThemeEnum.FOOTER
        );

        return _autoCompleteCall;
    }

    private _useLocalList(searchString: string): void {
        const _searchString = searchString.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
        this._$inputWidth.next(this.searchInput.nativeElement.getBoundingClientRect().width);
        const newLocalList = this._$lastResultSet.getValue().filter((item) => {
            const _regex = new RegExp(`(^${_searchString}|\\W${_searchString})+`, 'gi');
            return `${item.label}`.match(_regex);
        });
        this._setNewList(newLocalList);
    }

    private _setNewList(newList: Array<AutoCompleteListItemInterface>): void {
        if (this.type === AutoCompleteTypeEnum.REQUEST) {
            this._$results.next(newList);
            this._$lastResultSet.next(newList);
        } else {
            this._$results.next(newList);
            this._$lastResultSet.next(this.config.listItems);
        }
        setTimeout(() => {
            if (this._changeDetectorRef && !(this._changeDetectorRef as ViewRef).destroyed) {
                this._changeDetectorRef.markForCheck();
                this._changeDetectorRef.detectChanges();
            }
        });
    }

    public clear() {
        this.searchInput.nativeElement.value = '';
        this._searchTerm = '';
        this._useLocalList('');
        this._$searchString.next('');
        if (this.config.theme === AutoCompleteThemeEnum.FILTER) {
            this.selected.emit({
                label: '',
                value: ''
            });
        }
    }

    public focus() {
        this.searchInput.nativeElement.focus();
    }

    public triggerFocus() {
        this.onFocus.emit();
    }

    public triggerBlur() {
        this.onBlur.emit();
    }

    public listFocus(e) {
        if (e.keyCode === 40) {
            document.getElementById('element0').focus();
            document.getElementById('element0').classList.add('active');
            this.currentIndex = 0;
        }
    }

    public listFocusNext(e) {
        let lengthList = document.getElementsByClassName('search-element').length;
        if (document.getElementById('element' + this.currentIndex))
            document.getElementById('element' + this.currentIndex).classList.remove('active');
        if (e.keyCode === 40) {
            this.currentIndex = this.currentIndex + 1;

            if (this.currentIndex <= lengthList - 1 && this.currentIndex >= 0 && document.getElementById('element' + this.currentIndex)) {
                document.getElementById('element' + this.currentIndex).focus();
                document.getElementById('element' + this.currentIndex).classList.add('active');
            } else {
                this.currentIndex = 0;
                if (document.getElementById('element' + this.currentIndex)) {
                    document.getElementById('element' + this.currentIndex).focus();
                    document.getElementById('element' + this.currentIndex).classList.add('active');
                }
            }
        } else if (e.keyCode === 38) {
            this.currentIndex = this.currentIndex - 1;
            if (this.currentIndex <= lengthList - 1 && this.currentIndex >= 0 && document.getElementById('element' + this.currentIndex)) {
                document.getElementById('element' + this.currentIndex).focus();
                document.getElementById('element' + this.currentIndex).classList.add('active');
            } else {
                this.currentIndex = lengthList - 1;
                if (document.getElementById('element' + this.currentIndex)) {
                    document.getElementById('element' + this.currentIndex).focus();
                    document.getElementById('element' + this.currentIndex).classList.add('active');
                }
            }
        }
    }

    public valuechange(valueTyped) {
        this._searchTerm = valueTyped;
        this.searchInput.nativeElement.value = this._searchTerm;
        this.searchInput.nativeElement.scrollIntoView();
        this.resultsHidden = true;
    }
}
